/**
 * CLOTH bone-chain simulation — drives the simulated cloth bones the male-body build
 * appends to the rig (maleRig.ts MALE_CLOTH; generated by build-male.mts from any piece
 * with a `cloth` config — the drifter cloak today, capes/banners/coats later).
 *
 * Model: per chain, the ROOT joint is pinned to its animated anchor bone (the collar);
 * the remaining joints are verlet particles — inertia + gravity + a light flutter — pulled
 * gently toward the ANCHOR-RELATIVE BIND DRAPE (shape retention), distance-constrained
 * down the chain, laced to the NEIGHBOUR chains with tension-only lateral constraints
 * (stretch resists, folding is free — that's what makes it fabric, not four ropes), and
 * pushed out of CAPSULE COLLIDERS bound to the skeleton (torso + limbs). Bone-bound
 * capsules follow the animated pose, so the cloth drapes correctly at ANY body
 * orientation — swimming prone, the cape lies on the back instead of falling through the
 * chest — and swinging arms/legs push the cloth aside instead of clipping through it.
 * Each capsule is slackened per node to the node's AUTHORED bind distance, so collision
 * never pops the cloth off its authored drape at rest.
 *
 * REST-ADAPTIVE settling: shape retention + flutter are scaled by an apparent-motion factor
 * (body translation + wind, smoothed → restK). Stood still in still air (restK≈1) the chains
 * firm up to REST_SHAPE and the flutter dies, so the cloak hangs CALM instead of swinging to
 * every idle breath (the old "flutters all over the place while standing" — a small anchor
 * sway resonating through an under-damped chain). In motion / wind (restK→0) it reverts to
 * the loose per-piece `shape` and full flutter, so it still trails and flows — the motion
 * response is untouched. Frame-local speed, so standing on a spinning planet still reads rest.
 *
 * Bone rotations are written as SWING-ONLY deltas from the bind orientation: the rotation
 * that takes the bind segment direction to the simulated one, applied on top of the bind
 * local. Using a raw axis-to-direction quaternion here rolls the panels about the chain
 * axis (the mesh was skinned against the bind frame) and the cloth folds into a twisted
 * tangle — the swing-delta form keeps the lateral axes stable.
 *
 * Space: RENDER space (bones live there), with a teleport/floating-origin guard — a root
 * jump beyond SNAP_DIST translates the whole state instead of yanking the cloth. Ordinary
 * movement is NOT compensated, which is what makes the cloak trail and settle.
 *
 * ⚠⚠ The co-rotating `frame` (footPlanet.group) MUST hold THIS frame's pose AND a fresh
 * `matrixWorld` when step() reads it. system.update() poses the group (.position/.quaternion)
 * but does NOT recompute matrixWorld — the RENDERER does, at draw time, AFTER avatar.update().
 * So the caller MUST `frame.updateWorldMatrix(true, false)` right before step() (avatar.ts
 * does). Without it the cloth reads a ONE-RENDER-STALE frame: the planet's ~2 m/s spin carry
 * (a standing player IS carried) mismatches the fresh avatar position → the cape hem jumps
 * ~20 cm/frame in planet-local (measured; the outfitter has no frame so it never saw this —
 * that "outfitter looks calmer than the overworld" was the tell). Diagnose with the floating
 * overworld cloth test bed: window.__hl.devCloak(). reseed() before an avatar (re)load gives a
 * clean settled drape at the spawn pose instead of a frozen stale one exploding until it settles.
 */
import * as THREE from "three";
import type { MaleClothDef } from "./maleRig";

const _root = new THREE.Vector3();
const _down = new THREE.Vector3();
const _gq = new THREE.Quaternion();
const _v = new THREE.Vector3();
const _d = new THREE.Vector3();
const _t = new THREE.Vector3();
const _wq = new THREE.Quaternion();
const _lq = new THREE.Quaternion();
const _sw = new THREE.Quaternion();
const _am = new THREE.Matrix4();
const _iq = new THREE.Quaternion();
const _one = new THREE.Vector3(1, 1, 1);
const _fm = new THREE.Matrix4();
const _fq = new THREE.Quaternion();
const _fp = new THREE.Vector3();
const _fs = new THREE.Vector3();
const _windH = new THREE.Vector3();
const _upF = new THREE.Vector3(); // frame-space up (−_down), for horizontalising the cape dir

/** Per-substep pull of particle velocity toward the ambient air's motion (wind drag). */
const AIR_RATE = 8;

/** REST DETECTION: apparent-motion speed (body translation + wind, m/s) below REST_LO reads
 *  as "standing still in still air" → full settle; above REST_HI reads as "in motion / wind"
 *  → the loose flowing params. Between, a smoothstep. Tuned so a breathing idle sway (anchor
 *  moves ~0.1 m/s, body doesn't translate) counts as REST while a walk (>1 m/s) does not — so
 *  a stood-still cloak hangs calm instead of swinging to every breath, yet still trails when
 *  you move. `restShape`/flutter-gate are applied by this factor in substep(). */
const REST_LO = 0.35;
const REST_HI = 1.6;
/** Motion (m/s) at which the procedural flutter reaches full strength (0 at true rest — real
 *  fabric hangs still in dead air, flutters in a breeze / when carried through the air). */
const FLUT_LO = 0.4;
const FLUT_HI = 3;
/** Shape retention (per-1/60s pull toward the authored drape) at full REST. The per-piece
 *  `shape` is the IN-MOTION value (flowing); this is the calm-drape value it eases up to when
 *  the wearer is nearly still. Kept as one shared constant so every cloth piece settles the
 *  same; per-piece override could join MaleClothDef later if a piece needs it. */
const REST_SHAPE = 0.32; // 0.4 → 0.32 (2026-08-10): a touch softer at rest — the calm hang keeps, the drape reads fabric not board
const smoothstep = (lo: number, hi: number, x: number): number => {
  const t = Math.max(0, Math.min(1, (x - lo) / (hi - lo)));
  return t * t * (3 - 2 * t);
};

/** A root moving further than this in one frame is a teleport/origin rebase, not motion. */
const SNAP_DIST = 5;
/** Max root SPEED the cloth reacts to (m/s) — per frame the reaction budget is this × dt
 *  and the excess motion is carried rigidly. A RATE (not a per-frame distance) so the cap
 *  is frame-rate independent: at dive speeds every frame saturates to the same apparent
 *  wind instead of alternating react/skip with dt, and a hitch at sprint speed can't yank
 *  the chains (the "explosion" failure mode). Above sprint (11) + glide; dives saturate. */
const MAX_REACT_SPEED = 24;
/** Max total simulated time per frame (s) — hitches beyond this simply advance less. */
const MAX_DT = 0.1;
/** Max substep length (s): frames longer than this integrate in equal substeps. Verlet is
 *  only stable at a bounded step; combined with the time-corrected velocity below this
 *  makes the sim FRAME-RATE INDEPENDENT (variable dt was the jitter failure mode). */
const H_MAX = 1 / 40;

/** Body CAPSULE colliders: [boneA, boneB, radius(m)] segments between the two joints.
 *  Bound to the skeleton, so they carry the animated pose — this is what keeps the cloth
 *  off the body in any orientation and out of the way of swinging limbs. (Exported for the
 *  _clothharness.mts penetration metric.) */
export const CAPSULES: [string, string, number][] = [
  ["pelvis", "neck_01", 0.155],
  // SHOULDER BALL (2026-08-11). This list used to jump straight from the torso capsule to the
  // upper arm, leaving the deltoid — the widest part of the silhouette, and the part that
  // swings furthest — with NO collider at all. A cape simply passed through the shoulders
  // because nothing told it not to. No amount of skinning fixes that; the sim has to be told
  // what is there. Slack is still measured per node against the authored bind drape, so a
  // cape already resting on the shoulder is not popped off it.
  ["clavicle_l", "upperarm_l", 0.075],
  ["clavicle_r", "upperarm_r", 0.075],
  ["upperarm_l", "lowerarm_l", 0.06],
  ["upperarm_r", "lowerarm_r", 0.06],
  ["lowerarm_l", "hand_l", 0.05],
  ["lowerarm_r", "hand_r", 0.05],
  ["thigh_l", "calf_l", 0.085],
  ["thigh_r", "calf_r", 0.085],
  ["calf_l", "foot_l", 0.065],
  ["calf_r", "foot_r", 0.065],
];
/** Slackened capsules thinner than this are skipped (no useful surface left). */
const CAPSULE_MIN_R = 0.02;

/** BACKPEDAL hold (fixes the "avatar clips through the cape moving backwards" — see the
 *  _clothharness backpedal scenarios). A trailing cloth always lags toward where the wearer
 *  came from: moving forward that's DOWN THE BACK, the drape's home, so it streams clean; but
 *  moving BACKWARD (a camera-locked aim/combat/strafe backpedal) it's the FRONT, so the sheet
 *  is flung forward and the HEM swings across the legs/arms — which then poke through it (the
 *  harness clip-source shows the penetration is thigh/upperarm, not the chest). The fix holds
 *  the sheet: shape-retention firms toward the down-the-back drape in proportion to how hard
 *  the wearer moves INTO the cape, so a backpedal keeps the drape instead of flinging it.
 *  (A forward-offset chest collider and a back-hemisphere constraint were both tried and
 *  rejected — the clip is at the hem/limbs, not the chest, so the collider missed it and the
 *  constraint shoved the hem sideways INTO a thigh; holding the swing upstream is the real fix.)
 *  BACKPEDAL_SHAPE = extra shape-retention (added to effShape) at full backpedal; LO/HI = the
 *  into-cape speed band (m/s) it ramps in over. */
const BACKPEDAL_SHAPE = 0.6;
const BACKPEDAL_LO = 0.3;
const BACKPEDAL_HI = 2.5;

interface Chain {
  bones: THREE.Bone[];
  lens: number[];
  /** Bind local rotations + bind segment direction in each PARENT frame (constant). */
  bindLocal: THREE.Quaternion[];
  bindDirParent: THREE.Vector3[];
  /** Bind node positions in the ANCHOR's local frame (the rest drape, shape target). */
  bindNodeAnchor: THREE.Vector3[];
  /** Simulated joints: [0] = pinned root, [1..links] = particles (last = virtual hem end). */
  nodes: THREE.Vector3[];
  prev: THREE.Vector3[];
  /** Per (node, capsule) slack: how deep INSIDE the capsule the authored bind drape sits.
   *  The effective radius for that pair is capsule.r − slack — the node may come as close
   *  as it was authored, never closer, so collision can't pop the rest pose. */
  slack: Float32Array;
  /** Anchor pose at the END of the previous frame — substeps interpolate toward the current
   *  pose so root motion lands as N even impulses instead of one per-frame jump. */
  prevAnchorPos: THREE.Vector3;
  prevAnchorQuat: THREE.Quaternion;
  /** Anchor pose THIS frame (interpolation target), refreshed at the top of step(). */
  curAnchorPos: THREE.Vector3;
  curAnchorQuat: THREE.Quaternion;
  inited: boolean;
}

/** Distance from point p to segment ab; also writes the closest point into `out`. */
function segClosest(p: THREE.Vector3, a: THREE.Vector3, b: THREE.Vector3, out: THREE.Vector3): number {
  out.copy(b).sub(a);
  const len2 = out.lengthSq();
  const t = len2 > 1e-10 ? Math.max(0, Math.min(1, (p.x - a.x) * (out.x / len2) + (p.y - a.y) * (out.y / len2) + (p.z - a.z) * (out.z / len2))) : 0;
  out.multiplyScalar(t).add(a);
  return p.distanceTo(out);
}

export class ClothSim {
  private chains: Chain[] = [];
  private params: { damping: number; gravity: number; radius: number; flutter: number; shape: number }[] = [];
  private chainDef: number[] = [];
  /** Adjacent chain-column pairs [chainA, chainB, restLen per node] for the lateral lacing. */
  private lateral: { a: number; b: number; rest: Float32Array }[] = [];
  /** Resolved capsule colliders + their per-frame world segment endpoints. */
  private caps: { a: THREE.Bone; b: THREE.Bone; r: number; pa: THREE.Vector3; pb: THREE.Vector3 }[] = [];
  /** WORN-GEAR colliders (the back pack): swapped with the outfit, NO bind
   *  slack — pushing the drape OUT over the gear is the entire point. */
  private gearCaps: { a: THREE.Bone; b: THREE.Bone; r: number; pa: THREE.Vector3; pb: THREE.Vector3 }[] = [];
  private lastRoot = new THREE.Vector3();
  private hasLast = false;
  private t = 0;
  /** Last substep length (s) — for the time-corrected verlet velocity. */
  private lastH = 1 / 60;
  /** Ambient air velocity this frame (reference-frame space; zero = still air). */
  private wind = new THREE.Vector3();
  /** Smoothed apparent-motion speed (frame-local body translation + wind, m/s), and the rest
   *  factor derived from it (1 = stood still → settle to the calm drape, 0 = moving → flow). */
  private motionSpeed = 0;
  private restK = 1;
  /** Cape hang direction (anchor-LOCAL, from the bind drape) + the anchor bone it lives under.
   *  Lets the backpedal hold tell "into the cape" from "away from it" with no hard-coded body
   *  axis. Refreshed to frame-space (horizontal) each step into `backF`. */
  private backDirAnchor = new THREE.Vector3();
  private anchor: THREE.Object3D | null = null;
  private backF = new THREE.Vector3();
  /** 0..1 how hard the wearer is moving INTO the cape this frame (backpedal), driving the hold. */
  private backpedalK = 0;
  private haveBackF = false;

  /** Construct at BIND (Avatar ctor, matrices fresh): captures the rest drape + bind frames. */
  constructor(defs: readonly MaleClothDef[], boneByName: ReadonlyMap<string, THREE.Bone>) {
    for (const [an, bn, r] of CAPSULES) {
      const a = boneByName.get(an), b = boneByName.get(bn);
      if (a && b) this.caps.push({ a, b, r, pa: a.getWorldPosition(new THREE.Vector3()), pb: b.getWorldPosition(new THREE.Vector3()) });
    }
    defs.forEach((def, di) => {
      this.params.push({
        damping: def.damping,
        gravity: def.gravity,
        radius: def.radius,
        flutter: def.flutter,
        shape: def.shape,
      });
      const defFirst = this.chains.length;
      for (const c of def.chains) {
        const bones = c.bones.map((n) => boneByName.get(n)).filter((b): b is THREE.Bone => !!b);
        if (bones.length !== c.bones.length || !bones[0].parent) continue;
        const links = bones.length;
        const anchor = bones[0].parent;
        const anchorInv = anchor.matrixWorld.clone().invert();
        // bind node positions (world -> anchor local); end node extrapolated along the last bone
        const nodesW: THREE.Vector3[] = [];
        for (let k = 0; k < links; k++) nodesW.push(bones[k].getWorldPosition(new THREE.Vector3()));
        bones[links - 1].getWorldQuaternion(_wq);
        nodesW.push(
          new THREE.Vector3(1, 0, 0).applyQuaternion(_wq).multiplyScalar(c.lens[links - 1]).add(nodesW[links - 1]),
        );
        const bindNodeAnchor = nodesW.map((p) => p.clone().applyMatrix4(anchorInv));
        // bind locals + bind segment dirs in each parent's frame
        const bindLocal = bones.map((b) => b.quaternion.clone());
        const bindDirParent: THREE.Vector3[] = [];
        for (let k = 0; k < links; k++) {
          bones[k].parent!.getWorldQuaternion(_wq).invert();
          bindDirParent.push(nodesW[k + 1].clone().sub(nodesW[k]).normalize().applyQuaternion(_wq));
        }
        // per (node, capsule) slack: how deep the AUTHORED drape sits inside each capsule
        const slack = new Float32Array(nodesW.length * this.caps.length);
        this.caps.forEach((cap, cpi) => {
          for (let k = 0; k < nodesW.length; k++) {
            const d = segClosest(nodesW[k], cap.pa, cap.pb, _t);
            slack[k * this.caps.length + cpi] = Math.max(0, cap.r - d);
          }
        });
        this.chains.push({
          bones,
          lens: c.lens,
          bindLocal,
          bindDirParent,
          bindNodeAnchor,
          nodes: nodesW.map((p) => p.clone()),
          prev: nodesW.map((p) => p.clone()),
          slack,
          prevAnchorPos: new THREE.Vector3(),
          prevAnchorQuat: new THREE.Quaternion(),
          curAnchorPos: new THREE.Vector3(),
          curAnchorQuat: new THREE.Quaternion(),
          inited: false,
        });
        this.chainDef.push(di);
      }
      // lateral lacing between adjacent chain columns (builder emits them in column order):
      // rest lengths from the bind drape, enforced tension-only in step()
      for (let ci = defFirst; ci < this.chains.length - 1; ci++) {
        const A = this.chains[ci], B = this.chains[ci + 1];
        const n = Math.min(A.nodes.length, B.nodes.length);
        const rest = new Float32Array(n);
        for (let k = 0; k < n; k++) rest[k] = A.nodes[k].distanceTo(B.nodes[k]);
        this.lateral.push({ a: ci, b: ci + 1, rest });
      }
    });
    // Cape hang direction from the bind drape of the first chain (anchor-local): mean node
    // offset from the anchor. Self-calibrating, so the backpedal hold needs no body-axis guess.
    if (this.chains.length) {
      const c0 = this.chains[0];
      this.anchor = c0.bones[0].parent;
      for (let k = 1; k < c0.bindNodeAnchor.length; k++) this.backDirAnchor.add(c0.bindNodeAnchor[k]);
      this.backDirAnchor.multiplyScalar(1 / (c0.bindNodeAnchor.length - 1)).sub(c0.bindNodeAnchor[0]);
      if (this.backDirAnchor.lengthSq() > 1e-8) this.backDirAnchor.normalize();
    }
  }

  /** RE-SEED the cloth to a fresh warm-started drape on the NEXT step: clears every chain's
   *  init so the seed + 45-substep settle re-runs against the current pose, and forgets the
   *  root history so the (usually huge) spawn/teleport jump isn't read as motion. Call on any
   *  avatar (re)load into the world — disembark, login/restore, LOD-in — where the cloth would
   *  otherwise carry a stale, frozen drape and "explode" until it settles (the load freak-out).
   *  The next step's frame is already correct (system.update poses footPlanet.group first). */
  reseed(): void {
    for (const ch of this.chains) ch.inited = false;
    this.hasLast = false;
    this.motionSpeed = 0;
    this.restK = 1;
  }

  /** Advance the sim + write bone rotations. `group` = the avatar root (its up = planet up).
   *  `waterK` 0..1 = how submerged the wearer is: buoyancy + drag (the cape wafts instead
   *  of hanging). `wind` = ambient air velocity (reference-frame space; the outfit room
   *  uses it to preview motion, planet weather can drive it later). `frame` = CO-ROTATING
   *  REFERENCE (the foot planet's group): planets spin and floating-origin rebases move
   *  render space, so a sim in raw world coordinates sees carry motion as PHANTOM WIND —
   *  a standing character's cape would stream sideways. All sim state lives in the frame's
   *  local space, where the ground is still.
   *
   *  Integrator notes (the anti-jitter/anti-explosion core):
   *  - frames are split into equal SUBSTEPS of at most H_MAX, and the verlet velocity is
   *    TIME-CORRECTED (scaled by h/lastH, damping as a per-second rate) — without this,
   *    every frame-time fluctuation reads as a force change and the cloth vibrates;
   *  - root motion beyond the MAX_REACT_SPEED budget is carried RIGIDLY (state translated),
   *    so a hitch while sprinting can't yank the chains into orbit;
   *  - collision contact is INELASTIC (prev corrected by the same push), so projections
   *    don't get re-read as velocity next step. */
  step(dt: number, group: THREE.Object3D, waterK = 0, wind: THREE.Vector3 | null = null, frame: THREE.Object3D | null = null): void {
    if (!this.chains.length) return;
    dt = Math.min(Math.max(dt, 1e-4), MAX_DT);
    // reference frame: world -> frame-local converters (identity when no frame)
    if (frame) {
      frame.matrixWorld.decompose(_fp, _fq, _fs);
      _fm.copy(frame.matrixWorld).invert();
      _fq.invert();
    } else {
      _fm.identity();
      _fq.identity();
    }
    group.getWorldQuaternion(_gq).premultiply(_fq);
    _down.set(0, -1, 0).applyQuaternion(_gq);
    this.wind = wind ? this.wind.copy(wind) : this.wind.set(0, 0, 0);
    // refresh capsule collider segments from the animated skeleton (once per frame)
    for (const cap of this.caps) {
      cap.a.getWorldPosition(cap.pa).applyMatrix4(_fm);
      cap.b.getWorldPosition(cap.pb).applyMatrix4(_fm);
    }
    for (const cap of this.gearCaps) {
      cap.a.getWorldPosition(cap.pa).applyMatrix4(_fm);
      cap.b.getWorldPosition(cap.pb).applyMatrix4(_fm);
    }
    // frame-space cape hang direction (horizontal) for the backpedal hold: the bind drape's
    // direction carried by the live anchor pose, flattened against gravity so it reads the
    // pure fore/aft the wearer moves along.
    this.haveBackF = false;
    if (this.anchor) {
      this.anchor.getWorldQuaternion(_wq).premultiply(_fq);
      this.backF.copy(this.backDirAnchor).applyQuaternion(_wq);
      _upF.copy(_down).multiplyScalar(-1);
      this.backF.addScaledVector(_upF, -this.backF.dot(_upF)); // horizontalise
      if (this.backF.lengthSq() > 1e-8) { this.backF.normalize(); this.haveBackF = true; }
    }

    // root motion handling: full teleports (foot-planet/frame switches) translate ALL
    // state with zero reaction; sub-teleport spikes (hitch × sprint, dive speeds) react
    // only to the frame's rate budget.
    this.chains[0].bones[0].getWorldPosition(_root).applyMatrix4(_fm);
    if (this.hasLast) {
      // budget dt is capped at a 30fps frame: the rate stays exact for any real frame
      // cadence (so constant-speed dives read one consistent apparent wind), while a HITCH
      // (one frame swallowing 250ms of travel) only gets one normal frame's reaction.
      const budget = MAX_REACT_SPEED * Math.min(dt, 1 / 30);
      _v.copy(_root).sub(this.lastRoot);
      const d2 = _v.lengthSq();
      // backpedal detection: signed root speed along the cape's hang direction (+ = moving INTO
      // the cape). Captured before the carry-budget clamp below mutates _v.
      this.backpedalK = this.haveBackF && d2 < SNAP_DIST * SNAP_DIST
        ? smoothstep(BACKPEDAL_LO, BACKPEDAL_HI, this.backF.dot(_v) / dt)
        : 0;
      // REST DETECTION: frame-local body speed (a teleport/origin-rebase >SNAP_DIST is not
      // motion → excluded) + wind, smoothed, mapped to restK. Standing still in still air ->
      // restK≈1 (calm drape); walking/wind -> restK→0 (loose flow). Frame-local, so standing
      // on a spinning planet reads as rest (the co-rotating frame cancels the carry).
      const rawSpeed = d2 < SNAP_DIST * SNAP_DIST ? Math.sqrt(d2) / dt + this.wind.length() : this.wind.length();
      this.motionSpeed += (rawSpeed - this.motionSpeed) * (1 - Math.exp(-6 * dt));
      if (d2 > budget * budget) {
        if (d2 < SNAP_DIST * SNAP_DIST) _v.multiplyScalar(1 - budget / Math.sqrt(d2));
        for (const ch of this.chains) {
          ch.prevAnchorPos.add(_v); // keep the substep interpolation spanning only the rest
          for (let k = 0; k < ch.nodes.length; k++) {
            ch.nodes[k].add(_v);
            ch.prev[k].add(_v);
          }
        }
      }
    } else {
      this.motionSpeed = this.wind.length();
      this.backpedalK = 0;
    }
    this.restK = 1 - smoothstep(REST_LO, REST_HI, this.motionSpeed);
    this.lastRoot.copy(_root);
    this.hasLast = true;

    const steps = Math.min(4, Math.max(1, Math.ceil(dt / H_MAX)));
    const h = dt / steps;

    // frame prep per chain: refresh the anchor pose target, seed the drape on first use
    let warm = false;
    for (const ch of this.chains) {
      const anchor = ch.bones[0].parent!;
      ch.bones[0].getWorldPosition(ch.nodes[0]).applyMatrix4(_fm); // refreshes matrices too
      _am.copy(anchor.matrixWorld).premultiply(_fm);
      _am.decompose(ch.curAnchorPos, ch.curAnchorQuat, _v);
      if (!ch.inited) {
        for (let k = 0; k < ch.nodes.length; k++) {
          ch.nodes[k].copy(ch.bindNodeAnchor[k]).applyMatrix4(_am);
          ch.prev[k].copy(ch.nodes[k]);
        }
        ch.prevAnchorPos.copy(ch.curAnchorPos);
        ch.prevAnchorQuat.copy(ch.curAnchorQuat);
        ch.inited = true;
        warm = true;
      }
    }

    // buoyancy + drag while submerged: the cape wafts instead of hanging
    const gravK = 1 - 0.85 * waterK;
    const dragK = Math.exp(-6 * waterK * h);

    // WARM-START (standard game-cloth practice on spawn/teleport/LOD-in): pre-simulate
    // against the current pose so the FIRST rendered frame is already the settled drape —
    // without it the cape visibly falls from its seeded pose into place over the first
    // second ("sticks up, then drapes").
    if (warm) {
      for (let w = 0; w < 45; w++) this.substep(1 / 60, 1, gravK, dragK, 1);
      this.lastH = 1 / 60;
    }

    for (let s = 0; s < steps; s++) {
      // the h/lastH velocity rescale bridges the PREVIOUS substep's length — it applies
      // to the first substep of the frame only (later substeps all have length h)
      this.substep(h, (s + 1) / steps, gravK, dragK, s === 0 ? h / this.lastH : 1);
    }

    for (const ch of this.chains) {
      ch.prevAnchorPos.copy(ch.curAnchorPos);
      ch.prevAnchorQuat.copy(ch.curAnchorQuat);
      // write rotations: SWING-ONLY delta from bind (parent frame), chained down the links —
      // twist-free, so the skinned panels never roll about the chain axis (frame-local
      // throughout — the common rigid transform cancels in the bone-local result).
      // Degenerate segments (coincident nodes) keep the previous rotation, never NaN.
      ch.bones[0].parent!.getWorldQuaternion(_wq).premultiply(_fq);
      for (let k = 0; k < ch.bones.length; k++) {
        _d.copy(ch.nodes[k + 1]).sub(ch.nodes[k]);
        if (_d.lengthSq() > 1e-12) {
          _d.normalize();
          _d.applyQuaternion(_lq.copy(_wq).invert()); // simulated dir in parent frame
          _sw.setFromUnitVectors(ch.bindDirParent[k], _d);
          ch.bones[k].quaternion.copy(_sw).multiply(ch.bindLocal[k]);
        }
        _wq.multiply(ch.bones[k].quaternion); // world quat for the next link's parent frame
      }
    }
    this.lastH = h;
  }

  /** One integration substep of length h: verlet + shape retention per chain, chain
   *  distance constraints, tension-only lateral lacing, then capsule collision.
   *  `alpha` = where in the frame's anchor interpolation this substep lands (0..1];
   *  `velRescale` = h/lastH time correction (first substep of a frame only). */
  private substep(h: number, alpha: number, gravK: number, dragK: number, velRescale: number): void {
    this.t += h;
    // wind drag: pull each particle's velocity toward the air's own motion
    const windOn = this.wind.lengthSq() > 1e-6;
    const airK = windOn ? 1 - Math.exp(-AIR_RATE * h) : 0;
    if (windOn) _windH.copy(this.wind).multiplyScalar(h);
    for (let ci = 0; ci < this.chains.length; ci++) {
      const ch = this.chains[ci];
      const P = this.params[this.chainDef[ci]];
      const links = ch.bones.length;
      // per-substep factors (time-corrected: P.damping is the per-1/60s decay).
      // SHAPE RETENTION eases from the in-motion value up to REST_SHAPE as the wearer stills
      // (restK) — a stood-still cloak firms up to its calm drape instead of swinging to every
      // breath; a moving one keeps the loose flow. FLUTTER is gated by apparent motion, so it
      // vanishes in dead air and grows with speed/wind (real fabric hangs still, then ripples).
      const g = P.gravity * gravK * h * h;
      // shape-retention eases to REST_SHAPE as the wearer stills (restK); it additionally firms
      // toward the down-the-back drape when moving INTO the cape (backpedalK), so a backpedal
      // holds the sheet instead of flinging it forward across the legs/arms.
      const effShape = P.shape + (REST_SHAPE - P.shape) * this.restK + BACKPEDAL_SHAPE * this.backpedalK;
      const shapeK = 1 - Math.exp(-effShape * 60 * h);
      const flutterK = smoothstep(FLUT_LO, FLUT_HI, this.motionSpeed);
      const damp = Math.pow(P.damping, h * 60) * dragK * velRescale;
      // anchor pose INTERPOLATED across the frame's substeps: root motion arrives as N
      // even impulses instead of one per-frame jump (per-frame jumps of varying size are
      // themselves a jitter source at variable frame rates)
      _iq.slerpQuaternions(ch.prevAnchorQuat, ch.curAnchorQuat, alpha);
      _t.lerpVectors(ch.prevAnchorPos, ch.curAnchorPos, alpha);
      _am.compose(_t, _iq, _one);
      ch.nodes[0].copy(ch.bones[0].position).applyMatrix4(_am);
      for (let k = 1; k <= links; k++) {
        const p = ch.nodes[k];
        _v.copy(p).sub(ch.prev[k]).multiplyScalar(damp);
        ch.prev[k].copy(p);
        p.add(_v).addScaledVector(_down, g);
        if (windOn) p.addScaledVector(_t.copy(_windH).sub(_v), airK);
        const fl = P.flutter * flutterK * k * h * h;
        p.x += Math.sin(this.t * 2.3 + ci * 1.7 + k) * fl;
        p.z += Math.cos(this.t * 1.9 + ci * 2.4 + k * 1.3) * fl;
        // gentle pull toward where the authored drape sits under the current anchor pose
        _t.copy(ch.bindNodeAnchor[k]).applyMatrix4(_am);
        p.lerp(_t, shapeK);
      }
      // chain distance constraints (2 passes, root-anchored so they converge downward)
      for (let pass = 0; pass < 2; pass++) {
        for (let k = 1; k <= links; k++) {
          _d.copy(ch.nodes[k]).sub(ch.nodes[k - 1]);
          const len = _d.length() || 1e-6;
          ch.nodes[k].copy(ch.nodes[k - 1]).addScaledVector(_d, ch.lens[k - 1] / len);
        }
      }
    }
    // lateral lacing between neighbour chains, TENSION-ONLY: resist stretching apart,
    // fold freely — the difference between a sheet of fabric and four independent ropes
    for (const lat of this.lateral) {
      const A = this.chains[lat.a], B = this.chains[lat.b];
      for (let k = 1; k < lat.rest.length; k++) {
        _d.copy(B.nodes[k]).sub(A.nodes[k]);
        const len = _d.length();
        if (len <= lat.rest[k] || len < 1e-6) continue;
        _d.multiplyScalar(((len - lat.rest[k]) / len) * 0.5);
        A.nodes[k].add(_d);
        B.nodes[k].sub(_d);
      }
    }
    // capsule collision: push particles out of the bone-bound body/limb capsules —
    // INELASTIC (prev moves too, so pushes carry no velocity into the next step), and
    // slackened per (node, capsule) to the authored bind drape (never pops the rest pose).
    const nCaps = this.caps.length;
    for (const ch of this.chains) {
      const links = ch.bones.length;
      for (let k = 1; k <= links; k++) {
        const p = ch.nodes[k];
        for (let cpi = 0; cpi < nCaps; cpi++) {
          const cap = this.caps[cpi];
          const effR = cap.r - ch.slack[k * nCaps + cpi];
          if (effR < CAPSULE_MIN_R) continue;
          const d = segClosest(p, cap.pa, cap.pb, _t);
          if (d >= effR || d < 1e-5) continue;
          _v.copy(p).sub(_t).multiplyScalar((effR - d) / d);
          p.add(_v);
          ch.prev[k].add(_v);
        }
        // Worn-gear capsules: full radius, no slack — the cape lies OVER the
        // pack, not through it. Same inelastic push.
        for (const cap of this.gearCaps) {
          const d = segClosest(p, cap.pa, cap.pb, _t);
          if (d >= cap.r || d < 1e-5) continue;
          _v.copy(p).sub(_t).multiplyScalar((cap.r - d) / d);
          p.add(_v);
          ch.prev[k].add(_v);
        }
      }
    }
  }

  /** Swap the worn-gear colliders (outfit changed). Bone-name pairs + radius,
   *  resolved against the live skeleton; unknown bones are skipped. */
  setGearCapsules(defs: readonly (readonly [string, string, number])[], boneByName: ReadonlyMap<string, THREE.Bone>): void {
    this.gearCaps = [];
    for (const [an, bn, r] of defs) {
      const a = boneByName.get(an), b = boneByName.get(bn);
      if (a && b) this.gearCaps.push({ a, b, r, pa: new THREE.Vector3(), pb: new THREE.Vector3() });
    }
  }
}
